/**
 * One day as a column of hours, entries placed where they happened.
 *
 * The list says what you logged; this says what you missed. A gap between two
 * blocks is time nobody has accounted for, and with a calendar connected the
 * meetings sit beside the entries as outlines, so "the 2pm call never got
 * logged" is visible at a glance. Calendar events only reach back a few days
 * (CALENDAR_LOOKBACK_DAYS), so older days show entries alone.
 *
 * Tap a block to edit it, tap a meeting to log it.
 */

import { useMemo } from "react";

import { useTimer } from "@/context/TimerContext";
import { type CalendarEvent, useCalendarEvents } from "@/lib/calendarImport";
import { clock, deriveEntryLabel, entryDayKey, toIsoLocal } from "@/lib/timeRollup";
import type { Item, Project, TimeEntry } from "@/lib/types";

const HOUR_PX = 52;
/** The working day shown even when nothing falls outside it. */
const DEFAULT_FROM = 8;
const DEFAULT_TO = 18;

function hourOf(iso: string): number {
  const d = new Date(iso);
  return d.getHours() + d.getMinutes() / 60;
}

export function DayTimeline({ day, entries, projects, items, onEdit, onLogEvent }: {
  /** YYYY-MM-DD, local. */
  day: string;
  entries: TimeEntry[];
  projects: Project[];
  items: Item[];
  onEdit: (entry: TimeEntry) => void;
  onLogEvent: (event: CalendarEvent) => void;
}) {
  const timer = useTimer();
  const { query } = useCalendarEvents(projects);

  const rows = useMemo(
    () => entries.filter((e) => entryDayKey(e) === day && e.start)
      .sort((a, b) => a.start.localeCompare(b.start)),
    [entries, day],
  );
  const events = useMemo(
    () => (query.data?.events ?? []).filter((ev) => ev.date === day),
    [query.data, day],
  );
  const runningToday = timer.running && timer.startedAt && toIsoLocal(new Date(timer.startedAt)) === day;

  // Widen the window to whatever actually happened, early starts and late
  // finishes included, but never shrink it below the working day.
  const spans = [
    ...rows.map((e) => [hourOf(e.start), e.end ? hourOf(e.end) : hourOf(e.start) + e.durationSeconds / 3600]),
    ...events.map((ev) => [hourOf(ev.start), hourOf(ev.end)]),
    ...(runningToday ? [[hourOf(timer.startedAt!), hourOf(new Date().toISOString())]] : []),
  ];
  const from = Math.max(0, Math.min(DEFAULT_FROM, ...spans.map(([a]) => Math.floor(a))));
  const to = Math.min(24, Math.max(DEFAULT_TO, ...spans.map(([, b]) => Math.ceil(b))));
  const top = (h: number) => (h - from) * HOUR_PX;

  const hours: number[] = [];
  for (let h = from; h <= to; h++) hours.push(h);

  return (
    <div style={{ position: "relative", height: (to - from) * HOUR_PX + 12, marginTop: 6 }}>
      {hours.map((h) => (
        <div key={h} style={{ position: "absolute", left: 0, right: 0, top: top(h), display: "flex", alignItems: "center", gap: 8 }}>
          <span className="mono" style={{ width: 38, fontSize: 10.5, color: "var(--muted2)", textAlign: "right", transform: "translateY(-50%)" }}>
            {new Date(2000, 0, 1, h).toLocaleTimeString(undefined, { hour: "numeric" })}
          </span>
          <span style={{ flex: 1, height: 1, background: "var(--line)" }} />
        </div>
      ))}

      {/* Meetings: the right-hand lane, as outlines — they are not time yet. */}
      {events.map((ev) => (
        <button
          key={ev.id}
          onClick={() => onLogEvent(ev)}
          title={`${ev.summary} — tap to log`}
          style={{
            ...block, left: "calc(46px + 62%)", right: 0,
            top: top(hourOf(ev.start)), height: Math.max(22, (hourOf(ev.end) - hourOf(ev.start)) * HOUR_PX - 2),
            border: "1px dashed var(--line2)", background: "var(--surface)", color: "var(--ink3)",
          }}
        >
          <span style={blockTitle}>{ev.summary}</span>
        </button>
      ))}

      {rows.map((e) => {
        const p = projects.find((x) => x.id === e.project);
        const start = hourOf(e.start);
        const end = e.end ? hourOf(e.end) : start + e.durationSeconds / 3600;
        const h = Math.max(22, (end - start) * HOUR_PX - 2);
        return (
          <button
            key={e.id}
            onClick={() => onEdit(e)}
            style={{
              ...block, left: 46, right: events.length ? "calc(38% - 46px + 4px)" : 0,
              top: top(start), height: h,
              borderLeft: `3px solid ${p?.color ?? "var(--muted3)"}`,
              background: "var(--surface)", color: "var(--ink)",
            }}
          >
            <span style={blockTitle}>{deriveEntryLabel(e.note, items.find((i) => i.id === e.item)?.title)}</span>
            {h > 36 && (
              <span style={{ display: "block", fontSize: 11, color: "var(--muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {clock(e.durationSeconds)} · {p?.name ?? "No project"}
              </span>
            )}
          </button>
        );
      })}

      {runningToday && (() => {
        const start = hourOf(timer.startedAt!);
        const end = hourOf(new Date().toISOString());
        return (
          <div style={{
            ...block, left: 46, right: events.length ? "calc(38% - 46px + 4px)" : 0,
            top: top(start), height: Math.max(22, (end - start) * HOUR_PX - 2),
            border: "1px solid var(--accentLine)", background: "var(--accentTint)", color: "var(--accent)",
          }}>
            <span style={blockTitle}>Running…</span>
          </div>
        );
      })()}

      {rows.length === 0 && events.length === 0 && !runningToday && (
        <div style={{ position: "absolute", top: top(from) + HOUR_PX, left: 46, right: 0, textAlign: "center", fontSize: 13, color: "var(--muted)" }}>
          Nothing logged this day.
        </div>
      )}
    </div>
  );
}

const block: React.CSSProperties = {
  position: "absolute", boxSizing: "border-box", padding: "3px 8px", borderRadius: 6,
  // A <button> centres its content vertically; a block reads from its top.
  display: "flex", flexDirection: "column", justifyContent: "flex-start", alignItems: "stretch",
  textAlign: "left", cursor: "pointer", overflow: "hidden",
  border: "1px solid var(--line)", boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
};
const blockTitle: React.CSSProperties = {
  display: "block", fontSize: 12.5, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
};
